import { skipToken } from '@reduxjs/toolkit/query';
import { FormSection } from '@strata/react-form-section';
import { InlineMessage } from '@strata/react-inline-message';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';

import { CopayNoticeDetailsDialog } from 'components/wonReferrals/dialogs/CopayNoticeDetailsDialog';
import { TOCNoticeDetailsDialog } from 'components/wonReferrals/dialogs/TOCNoticeDetailsDialog';
import { SeeNoticeDetailsButton } from 'components/wonReferrals/SeeNoticeDetailsButton';
import { type TOCDetails, useGetTOCDetailsQuery } from 'store/api/claimApi';
import { type ReferralDetails } from 'store/api/treatmentApi';

interface NoticeBoardProps {
    referralDetails?: ReferralDetails;
    isPendingOutreach: boolean;
}

export const NoticeBoard = ({
    referralDetails = undefined,
    isPendingOutreach,
}: NoticeBoardProps) => {
    const { showTocOnNoticeBoard } = useFlags<{
        showTocOnNoticeBoard: boolean;
    }>();
    const { data: toc } = useGetTOCDetailsQuery(
        referralDetails && showTocOnNoticeBoard
            ? {
                  payerId: referralDetails.payerId,
                  referralId: referralDetails.referralId,
                  productServiceOid: referralDetails.productServiceOid,
              }
            : skipToken,
    );
    const [copayDetails, setCopayDetails] = useState<ReferralDetails['copayAssistanceMessage']>();
    const [tocDetails, setTocDetails] = useState<TOCDetails | undefined>();

    const showCopay = !!referralDetails?.copayAssistanceMessage;
    const showTOC = !isPendingOutreach && toc && showTocOnNoticeBoard;

    if (!showCopay && !showTOC) {
        return null;
    }

    return (
        <>
            <CopayNoticeDetailsDialog
                copayDetails={copayDetails}
                onSecondaryButtonClick={() => setCopayDetails(undefined)}
            />
            <TOCNoticeDetailsDialog
                tocDetails={tocDetails}
                onSecondaryButtonClick={() => setTocDetails(undefined)}
            />
            <div className="sds_details-page-overview__container" style={{ flex: '1 0' }}>
                <FormSection
                    sectionTitle="Notice Board"
                    isLoading={!referralDetails}
                    content={
                        <div className="flex__gap--medium" style={{ flexDirection: 'column' }}>
                            {showTOC ? (
                                <InlineMessage
                                    title="Transition of Care"
                                    label={
                                        <>
                                            Details about this patient&apos;s transition from
                                            another pharmacy are available.
                                            <SeeNoticeDetailsButton
                                                onClick={() => setTocDetails(toc)}
                                                buttonLabel="Review Transition Details"
                                            />
                                        </>
                                    }
                                    intent={InlineMessage.Intent.INFO}
                                    wrapText
                                />
                            ) : null}
                            {showCopay ? (
                                <InlineMessage
                                    title={referralDetails?.copayAssistanceMessage?.title}
                                    label={
                                        <>
                                            {referralDetails?.copayAssistanceMessage?.shortMessage}
                                            {referralDetails?.copayAssistanceMessage
                                                ?.longMessage ? (
                                                <SeeNoticeDetailsButton
                                                    onClick={() =>
                                                        setCopayDetails(
                                                            referralDetails?.copayAssistanceMessage,
                                                        )
                                                    }
                                                />
                                            ) : null}
                                        </>
                                    }
                                    intent={InlineMessage.Intent.INFO}
                                    wrapText
                                />
                            ) : null}
                        </div>
                    }
                />
            </div>
        </>
    );
};
